import { CardHeader } from '../Card';
import React, { useContext } from 'react';
import Honeybadger from 'honeybadger-js';
import { useHistory, useLocation } from 'react-router-dom';
import { Snackbar } from '@xo-union/tk-component-alerts';

import {
  dashboardRoute,
  pageSettingsRoute,
  previewPageRouteWithOpenEditRail,
  previewRoute,
} from '../../helpers/routes';
import { TrackingContext } from '../../TrackingContext';
import { Page } from '../PageCard';
import { deletePageUpdate } from '../ManagePagesRail/deletePageUpdate';
import { SnackbarInterface, DISMISS_TIMEOUT } from '../../helpers/snackbar';
import { useUpdatePageMutation, useDeletePageMutation } from '../../generated/graphql';
import useComingSoonPage from '../../helpers/useComingSoonPage';
import { FeatureToggleEnum, useFeatureTogglesContext } from '../../context/FeatureTogglesContext';

interface Props {
  page: Page;
  preview?: boolean;
  open?: boolean;
  toggleOpen?: () => void;
  guidance?: boolean;
  hasDropdown?: boolean;
}

const CUSTOM_PAGE_TYPE = 'CustomPage';

const PageHeader: React.FC<Props> = ({ page, preview = false, hasDropdown = true, ...rest }) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const { track } = useContext(TrackingContext);
  const { isEnabled } = useFeatureTogglesContext();
  const [updatePage] = useUpdatePageMutation();
  const [deletePage] = useDeletePageMutation();
  const snackbar = React.useRef<SnackbarInterface>();
  const addComingSoon = useComingSoonPage(page);
  const shouldUseEditorRollout = isEnabled(FeatureToggleEnum.EditorRollout);

  const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    history.push(pageSettingsRoute(page.id, preview));
    track('Item Action Initiated', {
      action: 'edit',
      actionName: 'Edit Page',
      page: page.title,
      item: null,
      source: 'mini menu',
    });
  };

  const previewOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const editPreviewPath = previewPageRouteWithOpenEditRail(page.routeName || '');
    const isDashboardContext = pathname.startsWith(dashboardRoute) && !pathname.startsWith(previewRoute);

    if (shouldUseEditorRollout && isDashboardContext) {
      window.location.assign(editPreviewPath);
    } else {
      history.push(editPreviewPath);
    }

    track('Website Preview', { source: 'page', page: page.title });
  };

  const toggleVisibility = async (showMessage?: boolean, messageInfo?: string) => {
    try {
      await updatePage({
        variables: {
          id: page.id as number,
          attributes: { show: !page.show },
        },
        update(_cache, { data }) {
          track('WWS Page Edited', {
            pageType: data?.updatePage?.type,
            action: 'edit',
            actionName: 'visibility',
            pageVisible: data?.updatePage?.show,
            source: 'mini menu',
          });
        },
      });

      //show message
      if (showMessage && messageInfo && snackbar?.current) {
        snackbar.current.showAndDismiss({
          data: messageInfo,
          dismissTimeout: DISMISS_TIMEOUT,
        });
      }
    } catch (error) {
      Honeybadger.notify(error, {
        message: 'Error updating page',
        context: {
          error,
        },
      });
    }
  };

  const removePage = async () => {
    if (page.id) {
      try {
        await deletePage({
          variables: { id: page.id },
          update(cache, { data }) {
            deletePageUpdate(cache, data);
          },
        });
      } catch (error) {
        Honeybadger.notify(error, {
          message: 'Error deleting page',
          context: {
            error,
          },
        });
      }
    }
  };

  return (
    <>
      <Snackbar ref={snackbar} />
      <CardHeader
        page={page}
        onClick={onClick}
        previewOnClick={previewOnClick}
        toggleVisibility={toggleVisibility}
        removePage={page.type === CUSTOM_PAGE_TYPE ? removePage : null}
        hasDropdown={hasDropdown}
        subHeadOnClick={addComingSoon}
        {...rest}
      />
    </>
  );
};

export default PageHeader;
